import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
    LpsActivityApiModel,
    LpsConstraintApiModel,
    LpsWeeklyPlanApiModel,
} from '@/core/models/api/lps.model';

export enum LpsActionTypes {
    // Dashboard
    LoadDashboard = 'Load Dashboard',
    LoadDashboardSuccess = 'Load Dashboard Success',
    LoadDashboardFail = 'Load Dashboard Fail',

    // Activities
    LoadActivities = 'Load Activities',
    LoadActivitiesSuccess = 'Load Activities Success',
    LoadActivitiesFail = 'Load Activities Fail',
    CreateActivity = 'Create Activity',
    CreateActivitySuccess = 'Create Activity Success',
    UpdateActivity = 'Update Activity',
    UpdateActivitySuccess = 'Update Activity Success',

    // Constraints
    LoadConstraints = 'Load Constraints',
    LoadConstraintsSuccess = 'Load Constraints Success',
    LoadConstraintsFail = 'Load Constraints Fail',
    AddConstraint = 'Add Constraint',
    AddConstraintSuccess = 'Add Constraint Success',
    ReleaseConstraint = 'Release Constraint',
    ReleaseConstraintSuccess = 'Release Constraint Success',

    // Weekly Plan
    LoadWeeklyPlan = 'Load Weekly Plan',
    LoadWeeklyPlanSuccess = 'Load Weekly Plan Success',
    LoadWeeklyPlanFail = 'Load Weekly Plan Fail',
    CommitActivity = 'Commit Activity',
    CommitActivitySuccess = 'Commit Activity Success',

    // Metrics
    LoadPpc = 'Load Ppc',
    LoadPpcSuccess = 'Load Ppc Success',
    LoadCncAnalysis = 'Load Cnc Analysis',
    LoadCncAnalysisSuccess = 'Load Cnc Analysis Success',

    // Milestones (from activities with isMilestone=true via /Lps/milestones)
    LoadMilestones = 'Load Milestones',
    LoadMilestonesSuccess = 'Load Milestones Success',

    // Clear
    ClearLps = 'Clear Lps'
}

export const LpsActions = createActionGroup({
    source: 'Lps',
    events: {
        // Dashboard (loads activities, constraints, weeklyPlan, ppc, milestones in one shot)
        [LpsActionTypes.LoadDashboard]: props<{ projectId: number; year: number; weekNumber: number }>(),
        [LpsActionTypes.LoadDashboardSuccess]: props<{
            activities: LpsActivityApiModel[];
            constraints: LpsConstraintApiModel[];
            weeklyPlan: LpsWeeklyPlanApiModel[];
            ppc: any;
            milestones: LpsActivityApiModel[];
        }>(),
        [LpsActionTypes.LoadDashboardFail]: props<{ error: string }>(),

        // Activities
        [LpsActionTypes.LoadActivities]: props<{ projectId: number; statusId?: number; startDate?: Date; endDate?: Date }>(),
        [LpsActionTypes.LoadActivitiesSuccess]: props<{ activities: LpsActivityApiModel[] }>(),
        [LpsActionTypes.LoadActivitiesFail]: props<{ error: string }>(),
        [LpsActionTypes.CreateActivity]: props<{ activity: LpsActivityApiModel }>(),
        [LpsActionTypes.CreateActivitySuccess]: props<{ activity: LpsActivityApiModel }>(),
        [LpsActionTypes.UpdateActivity]: props<{ id: number; activity: LpsActivityApiModel }>(),
        [LpsActionTypes.UpdateActivitySuccess]: props<{ activity: LpsActivityApiModel }>(),

        // Constraints
        [LpsActionTypes.LoadConstraints]: props<{ projectId: number }>(),
        [LpsActionTypes.LoadConstraintsSuccess]: props<{ constraints: LpsConstraintApiModel[] }>(),
        [LpsActionTypes.LoadConstraintsFail]: props<{ error: string }>(),
        [LpsActionTypes.AddConstraint]: props<{ constraint: LpsConstraintApiModel }>(),
        [LpsActionTypes.AddConstraintSuccess]: props<{ constraint: LpsConstraintApiModel }>(),
        [LpsActionTypes.ReleaseConstraint]: props<{ constraintId: number; userId: number }>(),
        [LpsActionTypes.ReleaseConstraintSuccess]: props<{ constraint: LpsConstraintApiModel }>(),

        // Weekly Plan
        [LpsActionTypes.LoadWeeklyPlan]: props<{ projectId: number; year: number; weekNumber: number }>(),
        [LpsActionTypes.LoadWeeklyPlanSuccess]: props<{ weeklyPlan: LpsWeeklyPlanApiModel[] }>(),
        [LpsActionTypes.LoadWeeklyPlanFail]: props<{ error: string }>(),
        [LpsActionTypes.CommitActivity]: props<{ activityId: number; year: number; weekNumber: number; asCommitted: boolean; daysOfWeek: string[] }>(),
        [LpsActionTypes.CommitActivitySuccess]: emptyProps(),

        // Metrics
        [LpsActionTypes.LoadPpc]: props<{ projectId: number; year: number; weekNumber: number }>(),
        [LpsActionTypes.LoadPpcSuccess]: props<{ ppc: any }>(),
        [LpsActionTypes.LoadCncAnalysis]: props<{ projectId: number; year: number; weekNumber: number }>(),
        [LpsActionTypes.LoadCncAnalysisSuccess]: props<{ cncAnalysis: any }>(),

        // Milestones — same shape as LpsActivityApiModel (isMilestone=true)
        [LpsActionTypes.LoadMilestones]: props<{ projectId: number }>(),
        [LpsActionTypes.LoadMilestonesSuccess]: props<{ milestones: LpsActivityApiModel[] }>(),

        // Clear
        [LpsActionTypes.ClearLps]: emptyProps(),
    }
});
